<svelte:options customElement="dst-rl-timestamp" />

<script lang="ts">
	import languageStrings from '$i18n/de';
	import { onDestroy } from 'svelte';

	import {
		TIMESTAMP_UNITS,
		isLastHour,
		isThisYear,
		isToday,
		isYesterday,
		getViennaNow,
	} from './date';

	export let date: string | undefined = undefined;
	/** Optional: Reference date to determine if 'updated' should be displayed before the timestamp". */
	export let created: string | undefined = undefined;

	const viennaNow: Date = getViennaNow();
	const timestamp = date ? new Date(date) : new Date();
	const createdDateTimestamp = created ? new Date(created) : null;
	const isUpdate = createdDateTimestamp
		? timestamp.getTime() !== createdDateTimestamp?.getTime()
		: false;

	let interval: NodeJS.Timeout;

	$: isRelative = true;
	$: formattedDate = formatDate(timestamp);

	// update every minute if relative date (first 60 minutes)
	$: if (isRelative) {
		interval = setInterval(() => {
			formattedDate = formatDate(timestamp);
		}, TIMESTAMP_UNITS.minute);
	} else {
		clearInterval(interval);
	}

	onDestroy(() => {
		clearInterval(interval);
	});

	const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' };
	const formatPrefix = isUpdate ? languageStrings.timestamp.updated : '';

	const formatDate = (date: Date) => {
		// not this year
		if (!isThisYear(date, viennaNow)) {
			isRelative = false;
			return `${formatPrefix} ${date.toLocaleDateString('de-AT', { ...options, year: 'numeric', month: 'numeric', day: 'numeric' })}`;
		}

		// this year
		if (!isYesterday(date, viennaNow) && !isToday(date, viennaNow)) {
			isRelative = false;
			return `${formatPrefix} ${date.toLocaleDateString('de-AT', { ...options, month: 'numeric', day: 'numeric' })}`;
		}

		// yesterday
		if (isYesterday(date, viennaNow)) {
			isRelative = false;

			if (isUpdate) {
				return `${formatPrefix} ${date.toLocaleString('de-AT', { ...options, month: 'numeric', day: 'numeric' })}`;
			}

			return `${languageStrings.timestamp.yesterday}, ${date.toLocaleString('de-AT', options)}`;
		}

		// today
		if (isToday(date, viennaNow) && !isLastHour(date, viennaNow)) {
			isRelative = false;
			const prefix = Boolean(formatPrefix) ? `${formatPrefix} um` : formatPrefix;
			return `${prefix} ${date.toLocaleString('de-AT', options)}`;
		}

		// last hour
		const elapsed = date.getTime() - viennaNow.getTime();
		const rtf = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });

		if (Math.abs(elapsed) > TIMESTAMP_UNITS.minute) {
			return `${formatPrefix} ${rtf.format(Math.round(elapsed / TIMESTAMP_UNITS.minute), 'minute')}`;
		}

		if (isUpdate) {
			return `${formatPrefix} ${date.toLocaleString('de-AT', { ...options, hour: 'numeric', minute: 'numeric' })}`;
		}
		return `${languageStrings.timestamp.justNow}`;
	};
</script>

<time datetime={timestamp.toString()} title={timestamp.toLocaleString('de-AT')}>
	{formattedDate}
</time>
